<template>
  <div
    class="lpu-doctor-card-header d-flex flex-column align-center position-relative"
    :class="[isMobile ? 'lpu-doctor-card-header--full' : '', 'ga-3']"
  >
    <div
      v-if="doctor.is_exists_video_card || doctor.is_case_exists"
      class="lpu-doctor-card-header__anchor-links d-flex flex-column ga-2"
      :class="{ 'lpu-doctor-card-header__anchor-links--card-edge': isMobile }"
    >
      <v-tooltip
        v-if="doctor.is_case_exists"
        :left="!isMobile"
        :right="isMobile"
        open-on-hover
        :open-on-click="false"
      >
        <template #activator="{ on, attrs }">
          <a
            class="d-flex"
            v-bind="attrs"
            :href="`${doctorPageUrl}#cases`"
            v-on="on"
          >
            <img
              width="32"
              height="32"
              src="/static/_v1/pd/icons/ui-kit/doctor-cases.svg"
              :alt="tStatic('Иконка - кейсы врача')"
            >
          </a>
        </template>
        <span class="text-caption text-no-wrap bg-gray-0--text">
          {{ tStatic('Примеры работ') }}
        </span>
      </v-tooltip>
      <v-tooltip
        v-if="doctor.is_exists_video_card"
        :left="!isMobile"
        :right="isMobile"
        open-on-hover
        :open-on-click="false"
      >
        <template #activator="{ on, attrs }">
          <a
            class="d-flex"
            v-bind="attrs"
            :href="`${doctorPageUrl}#video-card`"
            v-on="on"
          >
            <img
              width="32"
              height="32"
              src="/static/_v1/pd/icons/ui-kit/video-card-play.svg"
              :alt="tStatic('Иконка - видео-визитка врача')"
            >
          </a>
        </template>
        <span class="text-caption text-no-wrap bg-gray-0--text">
          {{ tStatic('Видеовизитка') }}
        </span>
      </v-tooltip>
    </div>
    <div class="lpu-doctor-card-header__avatar-wrap flex-shrink-0">
      <a
        class="d-block text-decoration-none"
        :href="doctorPageUrl"
      >
        <img
          class="lpu-doctor-card-header__avatar"
          :src="avatarUrl"
          :alt="doctorName"
          :data-image-refresh-selector="String(doctor.id)"
        >
      </a>
    </div>

    <div
      v-if="context.request.feature_flags.ff_redesign_rating && stars"
      class="doctor-card-rating-v2 w-100"
    >
      <RatingRow
        :solidity="stars"
        :reviews-count="ratesCount"
        :reviews-href="`${doctorPageUrl}#otzivi`"
        centered
      />
    </div>

    <template v-else-if="stars">
      <div
        class="lpu-doctor-card-header__rating d-flex align-center justify-center pa-3 rounded-20 border-sm bg-gray-0"
      >
        <StarsRate :solidity="stars" />
      </div>

      <a
        v-if="ratesCount"
        class="text-body-2 link-decoration-underline text-secondary--text"
        :href="`${doctorPageUrl}#otzivi`"
      >
        {{ ratesCount }} {{ tStatic(reviewNoun) }}
      </a>
    </template>

    <div
      v-if="isStaff && !isMobile"
      class="lpu-doctor-card-header__staff d-flex flex-column align-center ga-1 mt-2"
    >
      <DoctorAvatarEditor
        :doctor-id="doctor.id"
        :image-selector="`[data-image-refresh-selector='${doctor.id}']`"
      >
        <button
          type="button"
          class="lpu-doctor-card-header__staff-btn text-body-2"
          data-qa="lpu_doctors_list_staff_avatar"
        >
          {{ tStatic('Аватар') }}
        </button>
      </DoctorAvatarEditor>
      <button
        type="button"
        class="lpu-doctor-card-header__staff-btn text-body-2"
        data-qa="lpu_doctors_list_staff_education"
        @click="openStaffDoctorEditDialog(staffEditUrl, 'doctoredit_education')"
      >
        {{ tStatic('Образование') }} ({{ doctor.educations_count || 0 }})
      </button>
      <button
        type="button"
        class="lpu-doctor-card-header__staff-btn text-body-2"
        data-qa="lpu_doctors_list_staff_job"
        @click="openStaffDoctorEditDialog(staffEditUrl, 'doctoredit_job')"
      >
        {{ tStatic('Опыт работы') }} ({{ doctor.jobs_count || 0 }})
      </button>
      <button
        type="button"
        class="lpu-doctor-card-header__staff-btn text-body-2"
        data-qa="lpu_doctors_list_staff_course"
        @click="openStaffDoctorEditDialog(staffEditUrl, 'doctoredit_course')"
      >
        {{ tStatic('Курсы') }} ({{ doctor.courses_count || 0 }})
      </button>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import useIsMobile from 'src/composables/useIsMobile'
import useContextStore from 'src/store/useContextStore'
import useURLStore from 'src/store/useURLStore'
import type { CommonContextStore } from 'src/types/context'
import DoctorAvatarEditor from 'src/components/common/DoctorAvatarEditor/DoctorAvatarEditor.vue'
import StarsRate from 'src/components/common/StarsRate/StarsRate.vue'
import RatingRow from 'src/components/common/RatingRow/RatingRow.vue'
import tStatic from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_functions/tStatic'
import tDynamic from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_functions/tDynamic'
import { getDoctorAvatarUrl } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuReviews/functions/getMediaUrl'
import declensionByNum from 'src/utils/declensionByNum'
import getDoctorNameField from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuDoctorsList/functions/getDoctorNameField'
import openStaffDoctorEditDialog from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuDoctorsList/functions/openStaffDoctorEditDialog'
import type { LpuDoctor } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuDoctorsList/types'

const props = defineProps<{
  doctor: LpuDoctor
  doctorPageUrl: string
}>()

const isMobile = useIsMobile()
const { context }: CommonContextStore = useContextStore()
const { reverse } = useURLStore()
const isStaff = computed(() => Boolean(context.request.user.is_staff))
const doctorName = computed(() => tDynamic(props.doctor, getDoctorNameField(props.doctor)))
const avatarUrl = computed(() => getDoctorAvatarUrl(props.doctor, context))
const stars = computed(() => Number(props.doctor.stars) || 0)
const ratesCount = computed(() => props.doctor.rates_count || 0)
const reviewNoun = computed(() => (
  declensionByNum(ratesCount.value, ['отзыв', 'отзыва', 'отзывов'])
))
const staffEditUrl = computed(() => {
  const fromStore = reverse({
    name: 'profile_doctors:staff_doctor_edit_profile',
    args: [props.doctor.id],
  })

  // На публичной LPU-странице URL staff-редактирования часто нет в URL-store.
  return fromStore || `/profile/doctors/doctor/${props.doctor.id}/edit_by_staff/`
})
</script>

<style lang="scss" scoped>
@import 'src/styles/variables';

.lpu-doctor-card-header {
  flex-shrink: 0;
  width: 150px;

  &__avatar-wrap {
    width: 150px;
    height: 150px;
  }

  &__anchor-links {
    position: absolute;
    left: -8px;
    top: -8px;
    z-index: 1;
  }

  &__anchor-links--card-edge {
    left: 0;
    top: 0;
  }

  &__avatar {
    display: block;
    width: 150px;
    height: 150px;
    object-fit: cover;
    border-radius: $border-radius-xl;
  }

  &__rating {
    width: 150px;
    box-sizing: border-box;
  }

  &__staff-btn {
    padding: 2px 8px;
    border: none;
    border-radius: $border-radius-sm;
    background-color: #FFF000;
    cursor: pointer;
    text-align: left;
  }
}

.lpu-doctor-card-header--full {
  width: 100%;
}
</style>
